// Admin API layer for HMS-INTERNAL-BACKEND
import http from './http';

function handleError(error) {
  const resData = error?.response?.data;
  if (resData) {
    const msg = resData.message || error?.message || 'Request failed';
    // If detailed field errors exist (Zod), include them
    const details = Array.isArray(resData.errors)
      ? resData.errors.map(e => e.field ? `${e.field}: ${e.message}` : e.message).join('; ')
      : (resData.error || '');
    const full = details ? `${msg} — ${details}` : msg;
    throw new Error(full);
  }
  throw new Error(error?.message || 'Request failed');
}

// POST /api/v1/admin/users { email, password, role, name, displayName, groups, permissions, additionalPermissions }
export async function createUser({ email, password, role, name, displayName, groups, permissions, additionalPermissions, ...rest }) {
  try {
    const userData = {
      email,
      password,
      role,
      name,
      displayName,
      groups,
      permissions,
      additionalPermissions,
      ...rest
    };
    const res = await http.post('/api/v1/admin/users', userData);
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// GET /api/v1/admin/users with query params
export async function listUsers({ page = 1, limit = 10, role = '', status = '', search = '' } = {}) {
  try {
    // Filter out empty string parameters to avoid validation errors
    const params = { page, limit };
    if (role && role.trim() !== '') params.role = role;
    if (status && status.trim() !== '') params.status = status;
    if (search && search.trim() !== '') params.search = search;
    
    const res = await http.get('/api/v1/admin/users', { params });
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// GET /api/v1/admin/users (alias for compatibility)
export async function getAllUsers({ page = 1, limit = 10, role = '', status = '', search = '' } = {}) {
  return await listUsers({ page, limit, role, status, search });
}

// PATCH /api/v1/admin/users/:id/status { status }
export async function updateUserStatus(userId, status) {
  try {
    const res = await http.patch(`/api/v1/admin/users/${userId}/status`, { status });
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// DELETE /api/v1/admin/users/:id
export async function deleteUser(userId) {
  try {
    const res = await http.delete(`/api/v1/admin/users/${userId}`);
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// GET /api/v1/admin/users/:id
export async function getUserById(userId) {
  try {
    const res = await http.get(`/api/v1/admin/users/${userId}`);
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// PUT /api/v1/admin/users/:id { email, role, name, status }
export async function updateUser(userId, userData) {
  try {
    const res = await http.put(`/api/v1/admin/users/${userId}`, userData);
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// GET /api/v1/admin/stats
export async function getUserStats() {
  try {
    const res = await http.get('/api/v1/admin/stats');
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// PUT /api/v1/admin/users/:id/reset-password
export async function resetUserPassword(userId) {
  try {
    const res = await http.post(`/api/v1/admin/users/${userId}/reset-password`, { newPassword: 'TempPassword123!' });
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// Dashboard Statistics
export async function getDashboardStats() {
  try {
    const res = await http.get('/api/v1/admin/dashboard/stats');
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// System Settings
export async function getSystemSettings() {
  try {
    const res = await http.get('/api/v1/admin/settings');
    return { data: res.data };
  } catch (e) { handleError(e); }
}

export async function updateSystemSettings(settings) {
  try {
    const res = await http.put('/api/v1/admin/settings', settings);
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// Backup Management
export async function createBackup() {
  try {
    const res = await http.post('/api/v1/admin/backup');
    return { data: res.data };
  } catch (e) { handleError(e); }
}

export async function getBackupHistory() {
  try {
    const res = await http.get('/api/v1/admin/backup/history');
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// Activity Logs
export async function getActivityLogs({ page = 1, limit = 10, action = '', user = '', startDate = '', endDate = '' } = {}) {
  try {
    const params = { page, limit };
    if (action && action.trim() !== '') params.action = action;
    if (user && user.trim() !== '') params.user = user;
    if (startDate && startDate.trim() !== '') params.startDate = startDate;
    if (endDate && endDate.trim() !== '') params.endDate = endDate;
    
    const res = await http.get('/api/v1/admin/activity-logs', { params });
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// System Health
export async function getSystemHealth() {
  try {
    const res = await http.get('/api/v1/admin/system/health');
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// Reports
export async function getReportsData({ type = '', startDate = '', endDate = '' } = {}) {
  try {
    const params = {};
    if (type && type.trim() !== '') params.type = type;
    if (startDate && startDate.trim() !== '') params.startDate = startDate;
    if (endDate && endDate.trim() !== '') params.endDate = endDate;
    
    const res = await http.get('/api/v1/admin/reports', { params });
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// Quick Actions
export async function performQuickAction(action, params = {}) {
  try {
    const res = await http.post(`/api/v1/admin/quick-actions/${action}`, params);
    return { data: res.data };
  } catch (e) { handleError(e); }
}

// Export admin API object for easier importing
export const adminAPI = {
  // Users
  createUser,
  listUsers,
  getAllUsers,
  updateUserStatus,
  deleteUser,
  getUserById,
  updateUser,
  getUserStats,
  resetUserPassword,
  
  // Dashboard
  getDashboardStats,
  
  // Settings
  getSystemSettings,
  updateSystemSettings,
  
  // Backup
  createBackup,
  getBackupHistory,
  
  // Logs & Health
  getActivityLogs,
  getSystemHealth,
  
  // Reports
  getReportsData,
  
  // Quick Actions
  performQuickAction
};
